import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import GPS_STRATEGY_TYPES from '@careerstart/wae-common/src/main/constants/gps';
import Box from '@mui/material/Box';

import { BLACK, PRIMARY_PURPLE } from '../../../../theme/colorConstants';
import SectionCard from '../../../positionTemplate/components/SectionCard';
import SwitchRow from '../../../positionTemplate/components/SwitchRow';
import {
  DEFAULT_EARLY_PUNCH_WINDOW,
  DEFAULT_PUNCH_ROUNDING,
  EARLY_PUNCH_WINDOW_OPTIONS,
  MIN_PUNCH_DISTANCE_METERS,
  PUNCH_ROUNDING_OPTIONS,
} from '../../constants';
import {
  IS_PRIORITY_RESOURCE_POOL_PERSISTED,
  PRIORITY_RESOURCE_POOL_FIELD,
} from '../../priorityResourcePoolAdapter';
import AutoSaveTextField from '../AutoSaveTextField';
import SaveStatusIndicator from '../SaveStatusIndicator';
import SectionTitle from '../SectionTitle';

import PunchWindowsRow from './PunchWindowsRow';

const SWITCH_KIND = { TOP_LEVEL: 'topLevel', FLAG: 'flag', PRP: 'prp' };

const STAFFING_SWITCHES = [
  { field: PRIORITY_RESOURCE_POOL_FIELD, kind: SWITCH_KIND.PRP },
  { field: 'requireEmployerApproval', kind: SWITCH_KIND.TOP_LEVEL },
  { field: 'allowBackfill', kind: SWITCH_KIND.TOP_LEVEL },
  { field: 'overtimeAllowed', kind: SWITCH_KIND.TOP_LEVEL },
  { field: 'isEnforcingAttendancePolicy', kind: SWITCH_KIND.FLAG },
];

const rootSx = { display: 'flex', flexDirection: 'column', gap: '16px' };

const punchDistanceSx = {
  backgroundColor: BLACK[10],
  borderBottom: `1px solid ${BLACK[20]}`,
  margin: '0 -16px',
  padding: '12px 16px 12px 32px',
};

const punchDistanceInnerSx = { borderLeft: `2px solid ${PRIMARY_PURPLE[30]}`, paddingLeft: '16px' };

const AdditionalInfoTab = ({ positionTemplate, saveField, getStatus }) => {
  const intl = useIntl();

  const { flags, gps, timekeeping } = positionTemplate;
  const isGpsEnforced = gps?.strategy !== GPS_STRATEGY_TYPES.UNENFORCED;
  const punches = timekeeping?.punches;
  const t = (id, values) => intl.formatMessage({ id }, values);

  const isSwitchOn = ({ field, kind }) =>
    !!(kind === SWITCH_KIND.FLAG ? flags?.[field] : positionTemplate[field]);

  const saveSwitch = ({ field, kind }, value) => {
    if (kind === SWITCH_KIND.FLAG) {
      saveField({
        key: field,
        fields: { flags: { [field]: value } },
        patch: { flags: { ...flags, [field]: value } },
      });
      return;
    }
    saveField({
      key: field,
      fields: { [field]: value },
      patch: { [field]: value },
      persist: kind !== SWITCH_KIND.PRP || IS_PRIORITY_RESOURCE_POOL_PERSISTED,
    });
  };

  const renderSwitch = (config, { divider = true } = {}) => (
    <SwitchRow
      key={config.field}
      text={t(`pt.detail.additionalInfo.${config.field}`)}
      subText={t(`pt.detail.additionalInfo.${config.field}.desc`)}
      checked={isSwitchOn(config)}
      divider={divider}
      adornment={<SaveStatusIndicator status={getStatus(config.field)} />}
      onChange={(event) => saveSwitch(config, event.target.checked)}
    />
  );

  const saveGps = (key, gpsFields) =>
    saveField({ key, fields: { gps: gpsFields }, patch: { gps: { ...gps, ...gpsFields } } });

  const savePunches = (key, group, leaf) =>
    saveField({
      key,
      fields: { timekeeping: { punches: { [group]: leaf } } },
      patch: {
        timekeeping: {
          ...timekeeping,
          punches: { ...punches, [group]: { ...punches?.[group], ...leaf } },
        },
      },
    });

  const validatePunchDistance = (value) => {
    const distance = Number(value);
    return Number.isInteger(distance) && distance >= MIN_PUNCH_DISTANCE_METERS
      ? null
      : t('pt.detail.additionalInfo.punchDistance.invalid', { min: MIN_PUNCH_DISTANCE_METERS });
  };

  const punchSelects = [
    {
      id: 'pt-detail-early-punch-window',
      label: t('pt.additionalDetails.earlyPunchWindow'),
      value: punches?.window?.inEarly ?? DEFAULT_EARLY_PUNCH_WINDOW,
      options: EARLY_PUNCH_WINDOW_OPTIONS,
      status: getStatus('earlyPunchWindow'),
      onChange: (inEarly) => savePunches('earlyPunchWindow', 'window', { inEarly }),
    },
    {
      id: 'pt-detail-punch-rounding',
      label: t('pt.additionalDetails.punchRounding'),
      value: punches?.rounding?.amount ?? DEFAULT_PUNCH_ROUNDING,
      options: PUNCH_ROUNDING_OPTIONS,
      status: getStatus('punchRounding'),
      onChange: (amount) => savePunches('punchRounding', 'rounding', { amount }),
    },
  ];

  return (
    <Box sx={rootSx}>
      <SectionCard header={<SectionTitle>{t('pt.detail.additionalInfo.staffing')}</SectionTitle>}>
        {STAFFING_SWITCHES.map((config, index) =>
          renderSwitch(config, { divider: index < STAFFING_SWITCHES.length - 1 })
        )}
      </SectionCard>

      <SectionCard
        header={<SectionTitle>{t('pt.detail.additionalInfo.timekeeping')}</SectionTitle>}
      >
        {renderSwitch({ field: 'isPunchingOutsideOfShiftEnabled', kind: SWITCH_KIND.FLAG })}
        <SwitchRow
          text={t('pt.detail.additionalInfo.gps')}
          subText={t('pt.detail.additionalInfo.gps.desc')}
          checked={isGpsEnforced}
          divider={!isGpsEnforced}
          adornment={<SaveStatusIndicator status={getStatus('gps.strategy')} />}
          onChange={(event) =>
            saveGps('gps.strategy', {
              strategy: event.target.checked
                ? GPS_STRATEGY_TYPES.ENFORCED
                : GPS_STRATEGY_TYPES.UNENFORCED,
            })
          }
        />
        {isGpsEnforced && (
          <Box sx={punchDistanceSx}>
            <Box sx={punchDistanceInnerSx}>
              <AutoSaveTextField
                id="pt-detail-punch-distance"
                label={t('pt.detail.additionalInfo.punchDistance')}
                helperText={t('pt.detail.additionalInfo.punchDistance.helper', {
                  min: MIN_PUNCH_DISTANCE_METERS,
                })}
                value={gps?.maxDistance === undefined ? '' : `${gps.maxDistance}`}
                status={getStatus('gps.maxDistance')}
                validate={validatePunchDistance}
                endAdornment={t('pt.detail.additionalInfo.punchDistance.unit')}
                inputWidth="170px"
                showValidIndicator
                onSave={(value) => saveGps('gps.maxDistance', { maxDistance: Number(value) })}
              />
            </Box>
          </Box>
        )}
        <PunchWindowsRow
          text={t('pt.detail.additionalInfo.punchWindows')}
          subText={t('pt.detail.additionalInfo.punchWindows.desc')}
          selects={punchSelects}
        />
      </SectionCard>
    </Box>
  );
};

AdditionalInfoTab.propTypes = {
  positionTemplate: PropTypes.shape({
    flags: PropTypes.shape({}),
    gps: PropTypes.shape({ strategy: PropTypes.string, maxDistance: PropTypes.number }),
    timekeeping: PropTypes.shape({
      punches: PropTypes.shape({
        window: PropTypes.shape({ inEarly: PropTypes.number }),
        rounding: PropTypes.shape({ amount: PropTypes.number }),
      }),
    }),
  }).isRequired,
  saveField: PropTypes.func.isRequired,
  getStatus: PropTypes.func.isRequired,
};

export default AdditionalInfoTab;
